import { useContext, useEffect, useRef } from 'react';
import { useForceUpdate } from '@/core/hooks/useForceUpdate.hook';
import { FluxContext } from '@/core/flux.context';
import _ from 'lodash';
import type { Store } from '@/core/flux.service';

function getState<T>(store: T, paths: (keyof T)[]) {
  return _.map(paths, (path) => [path, _.get(store, path)]);
}

/**
 * Similar to useFluxPath. Takes in multiple paths
 */
export function useFluxPaths<T>(store: T, paths: (keyof T)[], compareFn = Object.is): T {
  'use no memo'; // opts out this hook from being compiled by React Compiler
  const state = useRef(getState(store, paths));
  const flux = useContext(FluxContext);
  const [, forceUpdate] = useForceUpdate();

  useEffect(() => {
    flux.listenTo(
      store as unknown as Store,
      undefined,
      () => {
        // Object.is is the method react uses to bail out of state updates for useState and useReducer
        // https://reactjs.org/docs/hooks-reference.html#bailing-out-of-a-state-update
        for (let i = 0; i < state.current.length; i++) {
          const [path, value] = state.current[i];
          if (!compareFn(value, _.get(store, path))) {
            forceUpdate();
            state.current = getState(store, paths);

            return;
          }
        }
      },
      false,
    );
  }, [flux, store]);

  return store;
}
